import {debounce} from "lodash/function";
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import {proxyFilter} from "../classes/Filter";
import {FILTER_OPTIONS} from "../classes/FilterDataValidations";
import {checkIsFavorite} from "../../helpers/functions";
import PropertySearchRepository from "../../fixtures/PropertySearchRepository";
import PropertyInstanceSearchRepository from "../../fixtures/PropertyInstanceSearchRepository";
import ShowNotificationMixin from '@milestoneslabs/milestones-ui-kit/src/mixins/ShowNotificationMixin';
import {PROPERTY_LISTING_PAGINATION_UPDATED} from "../components/GroupPropertyListing/PropertyListing/PropertyListing";
import {GOOGLE_MAPS_MAP_MOVED} from "../components/GroupPropertySeachContent/PropertySearchMap/PropertySearchMap";
import {SEARCH_TABS} from "../../helpers/variables";
import IdleTrackerMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/IdleTrackerMixin";
import {mapGetters} from "vuex";
import {PENDING_AND_UNDER_CONTRACT} from "@milestoneslabs/milestones-core-js/src/AppResources/LuListingStatusAppResource";
import {ApiFilter, AppResourceSearch} from "@milestoneslabs/milestones-js-engine";
import PropertyAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/PropertyAppResource";
import PropertyInstanceAppResource
    from "@milestoneslabs/milestones-core-js/src/AppResources/PropertyInstanceAppResource";
import {GLOBAL_EVENTS} from '../../helpers/globalEvents';

export const MAP_PAGE_SIZE = 90;
export const PAGE_SIZE = 30;
export const CLEAR_FILTER_EVENT = 'PROPERTY_SEARCH_CLEAR_FILTER';
export const PROPERTY_SEARCH_HEADER_CLEAR_LABELS = 'PropertySearchHeader:clear-labels';
export const PROPERTY_SEARCH_HEADER_PROPERTIES_UPDATED = 'PropertySearchHeader:properties-updated';
export const PROPERTY_SEARCH_HEADER_PROPERTIES_UPDATED_MAP = 'PropertySearchHeader:properties-updated-map';
export const PROPERTY_SEARCH_HEADER_UPDATING_PROPERTIES = 'PropertySearchHeader:updating-properties';
export const PROPERTY_SEARCH_MAP_DISPLAY = 'PropertySearchHeader:map-display';
export const PROPERTY_SEARCH_MAP_TOGGLE = 'PropertySearchHeader:map-toggle';
export const PROPERTY_SEARCH_RESET = 'PropertySearchHeader:reset';
export const PROPERTY_SEARCH_MAP_FIT_BOUNDS = 'PropertySearchHeader:map-fit-bounds';
export const PROPERTY_SEARCH_FILTERS_CHANGED = 'PropertySearchHeader:filters-changed';
export const PROPERTY_SEARCH_DISABLED_OPTION = 'PropertySearch:disabled-option-fallback';
export const RELOAD_FAVORITES_MAP = 'PropertySearch:reload-favorits-map';

export const PROPERTY_SEARCH_START_SEARCHING_DISPLAY = 'PropertySearchHeader:start-searching-display';

export default {
    name: 'PropertySearchMixin',

    mixins: [
        ShowNotificationMixin,
        IdleTrackerMixin,
    ],

    props: {
        propertyDetailsRoute: {
            type: [String, Object],
            default: 'property.details.view',
        },
        searchConfig: {
            type: Object,
            default: () => {
                return {
                    apiPath: 'elasticsearch'
                };
            },
        }
    },

    data() {
        return {
            favoritesTabActive: false,
            savedSearchTabActive: false,
            filter: proxyFilter,
            propertyAppResourceSearch: new PropertySearchRepository(this.searchConfig),
            propertyInstanceAppResourceSearch: new PropertyInstanceSearchRepository(),
            propertyAppResourceSearchMap: new PropertySearchRepository(this.searchConfig),
            propertyInstanceAppResourceSearchMap: new PropertyInstanceSearchRepository(),
            searchInputValue: {
                value: (proxyFilter || {}).search || null,
                label: (proxyFilter || {}).searchLabel || '',
                field: null,
            },
            fitBounds: false,
            searchInProgress: false,
            inactiveUserTimeThreshold: 1500,
            userActivityThrottlerTime: 100,
            hjPendingListingStatuses: [],
        };
    },
    computed: {
        ...mapGetters({
            getCoreProperty: 'getCoreProperty',
            getHomeValueEstimate: 'getHomeValueEstimate',
        }),

        coreProperty() {
            //this fixes coreProperty instace of PropertyAppResource
            if (this.getHomeValueEstimate) {
                this.getCoreProperty.price = this.getHomeValueEstimate;
            }

            return new PropertyAppResource({
                uuid: this.getCoreProperty ? this.getCoreProperty.uuid : '',
                attributes: this.getCoreProperty ? this.getCoreProperty.attributes : {}
            });

        },

        isSold() {
            return this.filter.isSold;
        }
    },
    /**
     *  we fetch properties in beforeMount only if we don't have default selected values for any filter
     */
    beforeMount() {
        this.$bus.$on(RELOAD_FAVORITES_MAP, this.fetchPropertyInstanceAppResources);
        this.$bus.$on(PROPERTY_LISTING_PAGINATION_UPDATED, this.handlePaginationUpdated);
        this.$bus.$on(CLEAR_FILTER_EVENT, this.handleFilterClear);
        this.$bus.$on(GOOGLE_MAPS_MAP_MOVED, this.handleMapMoved);
        this.$bus.$on(PROPERTY_SEARCH_RESET, this.init);
    },

    beforeDestroy() {
        this.$bus.$off(RELOAD_FAVORITES_MAP, this.fetchPropertyInstanceAppResources);
        this.$bus.$off(PROPERTY_LISTING_PAGINATION_UPDATED, this.handlePaginationUpdated);
        this.$bus.$off(CLEAR_FILTER_EVENT, this.handleFilterClear);
        this.$bus.$off(GOOGLE_MAPS_MAP_MOVED, this.handleMapMoved);
        this.$bus.$off(PROPERTY_SEARCH_RESET, this.init);
    },

    mounted() {
        this.init();
        this.userActivityThrottler();
    },

    watch: {
        filter: {
            deep: true,
            handler(newVal) {
                if (newVal.favorite) {
                    this.inactiveUserTimeThreshold = 100;
                }
                this.searchInProgress = true;
                this.fitBounds = (this.filter.location || []).length === 0;
            },
        },

        isSold() {
            this.inactiveUserTimeThreshold = 100;
        },

        '$route.query': {
            deep: true,
            handler(newVal, oldVal) {
                var self = this;
                debounce(function () {
                    if (!self.favoritesTabActive && !self.savedSearchTabActive) {
                        self.init();

                        if (!newVal.hasOwnProperty('searchTab') || newVal.searchTab !== SEARCH_TABS.LIST) {
                            self.$bus.$emit(PROPERTY_SEARCH_MAP_DISPLAY);
                        }
                    }
                }, 100)();
            }
        }
    },

    methods: {
        async inactiveUserAction() {
            if (this.searchInProgress) {
                this.searchInProgress = false;
                await this.fetchProperties(this.favoritesTabActive, 1);
            }
            this.inactiveUserTimeThreshold = 1500;
        },

        init() {
            this.fetchHjListingStatuses();
            this.syncExpandedPropWithParent(true);
        },

        async fetchHjListingStatuses() {
            let hjListingStatusAppResourceSearch = new AppResourceSearch('home-junction-listing-status');
            hjListingStatusAppResourceSearch.setSearchData({
                pagination: 'disabled',
                filter: new ApiFilter({
                    params: {
                        lookupListingStatusUuid: PENDING_AND_UNDER_CONTRACT
                    }
                })
            }, false);
            try {
                await hjListingStatusAppResourceSearch.search();
                this.hjPendingListingStatuses = hjListingStatusAppResourceSearch
                    .appResourceCollection
                    .toArray()
                    .map(item => item.status);
            } catch (e) {
                console.error(e);
                this.showNotification({
                    message: 'Something went wrong. Please try again later',
                    type: 'error'
                });
            }
        },

        displayMap() {
            this.syncExpandedPropWithParent(true);
            this.fetchProperties(this.favoritesTabActive);
        },

        syncExpandedPropWithParent(isActive) {
            this.$emit('update:expanded', isActive);
        },

        handlePaginationUpdated(page) {
            this.fetchProperties(this.favoritesTabActive, page);
        },

        handleFilterClear(filterOption) {
            this.filter.clear(filterOption);
            if (!filterOption || filterOption.filterOption === '_search') {
                this.searchInputValue = {
                    label: '',
                    value: null,
                };
            }
            this.inactiveUserTimeThreshold = 1500;
            this.$bus.$emit(PROPERTY_SEARCH_HEADER_CLEAR_LABELS, filterOption);
        },

        handleMapMoved(coordinates) {
            this.inactiveUserTimeThreshold = 100;
            this.filter.search = null;
            this.filter.searchLabel = null;
            this.searchInputValue = {
                value: null,
                label: '',
            };
            this.filter.location = coordinates;
        },

        /**
         * @param requestInstances
         * @param page
         */
        fetchProperties(requestInstances = false, page) {
            return this.fetchPropertyAppResources(page, requestInstances);
        },

        /**
         * @param page
         * @returns {Promise<void>}
         */
        async fetchPropertyInstanceAppResources(page) {
            return await this.fetchPropertyAppResources(page, true);
        },

        propertyFilterRules(property, filterOutUuid = null) {
            property = property || {};
            if(!this.coreProperty.uuid) {
                return !filterOutUuid || property.uuid !== filterOutUuid;
            }
            return (this.coreProperty.uuid === property.uuid) ||
                (
                    property.importSource === 'system' ||
                    (
                        property.hasOwnProperty('details') &&
                        this.coreProperty.hasOwnProperty('details') &&
                        this.coreProperty.details.latitude !== property.details.latitude &&
                        this.coreProperty.details.longitude !== property.details.longitude
                    )
                );
        },

        propertyCheckRules(property) {
            if (!!property) {
                property.isPending = this.hjPendingListingStatuses.includes(property.status);
                property.isFavorite = checkIsFavorite(property);
            }
            return property;
        },


        async propertiesPostProcess(propertiesArray, instanceRequest = false, filterOutUuid = null, checkFirstImg = false) {
            let filterRule = data => this.propertyFilterRules(
                instanceRequest ? (data || {}).property : data,
                filterOutUuid
                );
            let mapCallback = data => {
                let property = this.propertyCheckRules(
                    instanceRequest ? (data || {}).property : data
                );
                return property;
            };
            return (propertiesArray || []).length === 0
                ? []
                : await Promise.all(
                    propertiesArray
                        .filter(filterRule)
                        .map(mapCallback)
                );
        },

        // eslint-disable-next-line max-len
        doSearchAndPostProcessing(resource, instanceRequest = false, injectCoreProperty = false, filterOutUuid = null, checkFirstImg = false) {
            return new Promise(async (resolve, reject) => {
                let properties = [];

                await resource.search();
                if (resource.length > 0) {
                    properties = resource.appResourceCollection.toArray();
                }


                if (Boolean(this.coreProperty) && this.coreProperty.uuid && injectCoreProperty) {
                    if(this.favoritesTabActive) {
                        properties.unshift(new PropertyInstanceAppResource({
                            attributes: {
                                property: this.coreProperty
                            }
                        }));
                    }else{
                        properties.unshift(this.coreProperty);
                    }
                }

                if (properties.length > 0) {
                    properties = await this.propertiesPostProcess(
                        properties,
                        instanceRequest,
                        filterOutUuid,
                        checkFirstImg
                    );
                }

                resolve(properties);
            });
        },

        /**
         * @param page
         * @param instanceRequest
         * @returns {Promise<void>}
         */
        async fetchPropertyAppResources(page, instanceRequest = false) {
            this.$bus.$emit(PROPERTY_SEARCH_HEADER_UPDATING_PROPERTIES);
            let resourceSearch = !instanceRequest
                ? this.propertyAppResourceSearch
                : this.propertyInstanceAppResourceSearch,

                resourceSearchMap = !instanceRequest
                    ? this.propertyAppResourceSearchMap
                    : this.propertyInstanceAppResourceSearchMap,

                filter = await this.filter.buildRequestFilterData();


            let propertyCoreUuid = null;
            let propertyCoreParentUuid = null;

            if (this.propertyCore && this.propertyCore.uuid) {
                filter.addParam('uuid', {value: this.propertyCore.uuid, operator: '!='});
                propertyCoreParentUuid = (this.propertyCore || {}).parentPropertyUuid || null;
            }

            let searchData = {
                pageSize: PAGE_SIZE,
                filter: filter,
                orderBy: this.filter.buildRequestOrderData(),
                withCount: this.filter.buildRequestCountData(),
            };

            if (instanceRequest) {
                searchData.include = ['property'];
            }
            if (page) {
                searchData.page = page;
            }

            let searchDataMap = cloneDeep(searchData);
            searchDataMap.pageSize = MAP_PAGE_SIZE;
            searchDataMap.page = Math.ceil(searchData.page * PAGE_SIZE / MAP_PAGE_SIZE);

            resourceSearch.setSearchData(searchData, false);
            resourceSearchMap.setSearchData(searchDataMap, false);

            let properties = [],
                propertiesMap = [];

            if (filter) {
                await Promise.all([
                    this.doSearchAndPostProcessing(
                        resourceSearch,
                        instanceRequest,
                        page === 1,
                        null,
                        true //only for listing properties
                    ),
                    this.doSearchAndPostProcessing(
                        resourceSearchMap,
                        instanceRequest,
                        true,
                        propertyCoreParentUuid,
                        false //no need here, on the map we have checks on image load
                    ),
                ])
                    .then(data => {
                        properties = data[0] || [];
                        propertiesMap = data[1] || [];
                    })
                    .catch(e => {
                        console.error(e);
                        this.showNotification({
                            message: 'Something went wrong. Please try again later',
                            type: 'error'
                        });
                    });
            }

            if (properties.length === 0 && this.favoritesTabActive) {
                return this.$bus.$emit(PROPERTY_SEARCH_START_SEARCHING_DISPLAY, true);
            }

            if (this.fitBounds && !instanceRequest) {
                this.$bus.$emit(PROPERTY_SEARCH_MAP_FIT_BOUNDS, true);
                this.fitBounds = false;
            }

            this.$bus.$emit(
                PROPERTY_SEARCH_HEADER_PROPERTIES_UPDATED,
                {
                    properties: properties,
                    meta: resourceSearch.meta,
                    search: this.searchInputValue.label,
                },
            );
            this.$bus.$emit(
                PROPERTY_SEARCH_HEADER_PROPERTIES_UPDATED_MAP,
                {
                    properties: propertiesMap,
                    meta: resourceSearchMap.meta,
                    search: this.searchInputValue.label,
                },
            );
            this.$bus.$emit(GLOBAL_EVENTS.HOMEOWNER_SEARCHED_PROPERTIES, this.filter);
        },

        setFilters(savedSearchAppResource) {
            let tempFilter,
                filters = savedSearchAppResource.data,
                filterKeys = Object.values(FILTER_OPTIONS);

            this.handleFilterClear();

            for (let key of filterKeys) {
                if (filters.hasOwnProperty(key)) {
                    tempFilter = filters[key];

                    if (
                        key === FILTER_OPTIONS.SEARCH &&
                        filters[key].field &&
                        filters[key].label &&
                        filters[key].value
                    ) {
                        tempFilter = {
                            nested: filters[key].field,
                            value: filters[key].value,
                        };
                        this.filter.searchLabel = filters[key].label;
                    }

                    this.filter[key] = tempFilter;
                }
            }

            this.fitBounds = (this.filter.location || []).length === 0;
            this.$bus.$emit(PROPERTY_SEARCH_MAP_DISPLAY, this.filter.location || []);
        },
    }
};
